import { Descriptions, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  createTestSubject,
  getTestType,
  updateTestSubjectSeq,
} from '../../../api/testCenter.api';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import InputSubjectModal from '../../../components/modals/InputSubjectModal';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IGetTestType,
  ITestTypeSubject,
} from '../../../models/testCenter.model';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import useAuth from '../../../hooks/useAuth';

interface IMatch {
  id: string;
}

const Block = styled.div`
  .button {
    .disable > img {
      background: darkgray;
    }
  }
`;

const group: string[] = ['Reading', 'Vocabulary', 'Grammar'];

const CbtTypeSubject = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const match = useRouteMatch<IMatch>();
  const examTypeCode = Number(match.params.id);
  const { successNotification, infoNotification } = useNotification();
  const { isDarkMode } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetTestType>();
  const {
    data: createRes,
    isLoading: createIsLoading,
    error: createError,
    execute: createExecute,
  } = useAxios();
  const {
    data: updateRes,
    isLoading: updateIsLoading,
    error: updateError,
    execute: updateExecute,
  } = useAxios();

  const [inputSubjectVisible, setInputSubjectVisible] = useState(false);
  const [subjects, setSubjects] = useState<ITestTypeSubject[]>([]);

  useEffect(() => {
    execute(getTestType(examTypeCode));
  }, []);

  useEffect(() => {
    if (res) {
      const _subjects = _.cloneDeep(res.data.subject);
      setSubjects(sortSubjects(_subjects) ?? []);
    }
  }, [res]);

  useEffect(() => {
    if (createRes) {
      execute(getTestType(examTypeCode));
      successNotification(t('common.message.add1'));
    }
  }, [createRes]);

  useEffect(() => {
    if (updateRes) {
      successNotification(t('common.message.save1'));
    }
  }, [updateRes]);

  const onCreateSubject = (req: any) => {
    createExecute(createTestSubject(examTypeCode, req));
  };

  const sortSubjects = (subjects?: ITestTypeSubject[]) =>
    subjects?.sort((a: any, b: any) =>
      a.seq < b.seq ? -1 : a.seq > b.seq ? 1 : 0,
    );

  const raiseSubject = (seq: number) => {
    const _subjects = [...subjects];
    const index = _subjects.findIndex((item) => item.seq === seq);
    _subjects[index].seq--;
    _subjects[index - 1].seq++;
    setSubjects(sortSubjects(_subjects) ?? []);
  };

  const lowerSubject = (seq: number) => {
    const _subjects = [...subjects];
    const index = _subjects.findIndex((item) => item.seq === seq);
    _subjects[index].seq++;
    _subjects[index + 1].seq--;
    setSubjects(sortSubjects(_subjects) ?? []);
  };

  const getSubjectTotalPoint = () => {
    let sum = 0;
    subjects.forEach((item: any) => {
      sum += item.totalPoint;
    });

    return sum;
  };

  const onUpdateSequence = () => {
    if (!subjects.length) {
      return infoNotification(t('cbt.type.message1'));
    }

    const req: any = [];
    const _group: string[] = [...group];
    let prevIndex: number | undefined = undefined;
    const subjectNames = subjects.map(({ subjectName }) => subjectName);
    let pass = false;
    group.forEach((value) => {
      if (!subjectNames.includes(value) && !pass) {
        pass = true;
      }
    });
    if (!pass) {
      subjects.forEach(({ subjectName }, index) => {
        const groupIndex = _group.findIndex((item) => subjectName === item);
        if (
          groupIndex !== -1 &&
          (prevIndex === undefined || prevIndex + 1 === index)
        ) {
          _group.splice(groupIndex, 1);
          prevIndex = index;
        }
      });
      if (!(_group.length === 3 || _group.length === 0)) {
        infoNotification(
          t('cbt.type.message2'),
        );
        return;
      }
    }

    subjects.forEach((item: ITestTypeSubject) => {
      req.push({
        examTypeSubjectCode: item.examTypeSubjectCode,
        seq: item.seq,
      });
    });

    updateExecute(updateTestSubjectSeq(examTypeCode, req));
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('cbt.type.column5'),
      dataIndex: 'subjectName',
      key: 'subjectName',
      align: 'center',
    },
    {
      title: t('cbt.type.column6'),
      dataIndex: 'seq',
      key: 'seq',
      align: 'center',
      render: (seq: number) => (
        <div className="order-sett__area">
          <a
            className={seq === 1 ? 'btn-order up disabled' : 'btn-order up'}
            onClick={() => {
              if (seq === 1) return;
              raiseSubject(seq);
            }}
          />
          <a
            className={
              res && seq === res.data.subject?.length ? 'btn-order down disabled' : 'btn-order down'
            }
            onClick={() => {
              if (res && seq === res.data.subject?.length) return;
              lowerSubject(seq);
            }}
          />
        </div>
      ),
    },
    {
      title: t('cbt.type.column7'),
      key: 'totalPoint',
      dataIndex: 'totalPoint',
      align: 'center',
    },
  ];

  return (
    <Block className="content scrollbar">
      {isLoading || createIsLoading || updateIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={createError} />
      <ErrorResponse error={updateError} />

      <div className='col-frame'>
        <SimpleHeader parentMenu={t('common.menu.cbt')} currentMenu={t('cbt.type.title1')} onlyTitle />
        <div className='comm-table__area rowType'>
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('cbt.type.column8')}>
              {res
                ? getCommonCodeLabel('TE0001', res.data.examDiv, 'ko')
                : null}
            </Descriptions.Item>
            <Descriptions.Item label={t('cbt.type.column2')}>
              {res?.data.examTypeName}
            </Descriptions.Item>
          </Descriptions>
        </div>

        <div className="comm-bx__space mt-5">
          <h3 className='sub-title'>- {t('cbt.type.title2')}</h3>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={subjects}
            pagination={{
              pageSize: 99999,
            }}
          />
        </div>
        <div className="comm-table__area rowType">
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('cbt.type.column9') } span={2}>
              {getSubjectTotalPoint()}
            </Descriptions.Item>
          </Descriptions>
        </div>
        <div className='comm-bx__space'>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic gray-line"
              onClick={() => history.push(`/cbt/type?eduStepCode=${res?.data?.eduStepCode}`)}
              text={t('common.button.list')}
            />
            <div className='ml-auto comm-btn__area'>
              <Button
                className="btn-basic pink-line ml-auto"
                onClick={() => setInputSubjectVisible(true)}
                text={t('common.button.add')}
                writeAuthYn={writeAuthYn}
                // test용 해제
                // disabled
              />
              <Button
                className="btn-basic pink"
                onClick={onUpdateSequence}
                text={t('cbt.type.button2')}
                writeAuthYn={writeAuthYn}
                // test용 해제
                // disabled
              />
            </div>
          </div>
        </div>
      </div>

      {inputSubjectVisible && (
        <InputSubjectModal
          visible={inputSubjectVisible}
          setVisible={setInputSubjectVisible}
          onCreateSubject={onCreateSubject}
        />
      )}
    </Block>
  );
};

export default CbtTypeSubject;
